import React, { useEffect, useState } from "react";
import { CardTitle, Col, Row } from "reactstrap";
import shallow from "zustand/shallow";
import * as yup from "yup";
import { setLocale } from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";

import formNavigationUtils from "../../../utils/formNavigationUtils";
import useCurrentCustomerStore from "../../../stores/CustomerStore";
import useCurrentSaleStore from "../../../stores/SaleStore";
import BusinessDetails from "../subFeatures/contactTabs/detailsTab/BusinessDetails";
import ChildrenModal from "../../../components/modals/ChildrenModal";
import CurrentAddress from "../subFeatures/contactTabs/addressTab/CurrentAddress";
import CustomerDetails from "../subFeatures/contactTabs/detailsTab/CustomerDetails";
import SaveNewCustomerButton from "../helperFunctions/SaveNewCustomerButton";
import ZipCityTable from "../../../components/tables/ZipCityTable";
import LicenseScanner from "./LicenseScanner";
import { getAdSources } from "../../../api/CustomerAPI";
import { useFromUser } from "../../../stores/LocalStorageHelper";
import { SaleTypeEnum } from "../../../constants/Constants";

const ContactAddPage = ({ updateDeal, isDeal = false, isBuyer }) => {
	const locationID = useFromUser("locationID");
	// Local states
	const [cityModal, setCityModal] = useState(false);
	const [addressData, setAddressData] = useState({});
	const [showScanner, setShowScanner] = useState(true);

	// Function to handle scanner close
	const handleScannerClose = () => {
		setShowScanner(false);
	};

	// Function to show the scanner
	const handleShowScanner = () => {
		setShowScanner(true);
	};

	// Opens and closes the city modal
	const toggleCityModal = () => setCityModal(!cityModal);

	// Customer store
	const {
		type,
		currentAddress,
		editCurrentAddress,
		editType,
		reset,

		// ─── Scanner-driven setters ────────────────────────────────────────
		editFirstName, // parsedData.firstName
		editMiddleName, // parsedData.middleName
		editLastName, // parsedData.lastName

		editLicense, // parsedData.licenseNumber
		editBirthDate, // parsedData.dob
		editLicExpires, // parsedData.expiration
		editGender, // parsedData.gender
		editAdSources,
	} = useCurrentCustomerStore(
		(state) => ({
			type: state.type,
			currentAddress: state.currentAddress,
			editCurrentAddress: state.editCurrentAddress,
			editType: state.editType,
			reset: state.reset,

			// Scanner mappings
			editFirstName: state.editFirstName,
			editMiddleName: state.editMiddleName,
			editLastName: state.editLastName,

			editLicense: state.editLicense,
			editBirthDate: state.editBirthDate,
			editLicExpires: state.editLicExpires,
			editGender: state.editGender,
			editAdSources: state.editAdSources,
		}),
		shallow
	);

	//sale store
	const { saleType } = useCurrentSaleStore(
		(state) => ({
			saleType: state.saleType,
		}),
		shallow
	);

	// Show modal containing cities with the 5 digit zip entered
	// Used to get the tax rate associated with a customer zip
	const handleZipChange = (zip, address) => {
		setAddressData({ zip, address });
		toggleCityModal();
	};

	useEffect(() => {
		reset();
		if (saleType === 1 || saleType === 2) {
			editType(1);
		}
		return () => {
			reset();
		};
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		getAdSources(locationID).then((res) => {
			editAdSources(res.data.content);
		});
		// eslint-disable-next-line
	}, []);

	// Define custom error messages for different error types
	setLocale({
		mixed: {
			required: "Required",
		},
		string: {
			// eslint-disable-next-line
			max: "Must be ${max} characters or less",
			matches: "Example Format: (123)456-7890",
		},
	});

	// Define rules for each input field
	const schema = yup.object().shape({
		firstName: yup.string().max(100),
		lastName: yup.string().required().max(100),
		contact: yup.string().max(25),
		source: yup.string().max(9),
		currentStreet: yup.string().max(50),
		currentCity: yup.string().max(50),
		mailStreet: yup.string().max(50),
		mailCity: yup.string().max(50),
		ssn: yup.string().matches(/^[0-9]{3}-[0-9]{2}-[0-9]{4}/, {
			message: "Must be 9 digits or empty",
			excludeEmptyString: true,
		}),
		email: yup.string().matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, {
			message: "Invalid Email",
			excludeEmptyString: true,
		}),
		// Removed phone validation - international formats vary too much
		currentZip: yup.string().required().max(10),
		licenseNumber: yup.string().max(20),
		dob: yup.string().max(50), // or stricter if needed
		expiration: yup.string().max(50),
		gender: yup.string().oneOf(["M", "F", ""]),
	});

	// Define form validation parameters
	const methods = useForm({
		reValidateMode: "onChange",
		resolver: yupResolver(schema),
	});

	return (
		<div onKeyDown={formNavigationUtils}>
			<Col className="pb-2 pt-3 text-center mobileHeading">
				<CardTitle>
					<h2>Add Customer</h2>
				</CardTitle>
			</Col>

			<FormProvider {...methods}>
				<div className="col pb-3 pt-0">
					<div className="row mx-0 noDesktop">
						<div className="col text-center">
							{showScanner ? (
								<LicenseScanner
									onClose={handleScannerClose}
									onScanComplete={(parsedData) => {
										const { setValue } = methods;
										const opts = { shouldValidate: true, shouldDirty: true };

										// ─── Names ────────────────────────────────────────────────────────
										if (parsedData.firstName) {
											setValue("firstName", parsedData.firstName, opts);
											editFirstName(parsedData.firstName);
										}
										if (parsedData.middleName) {
											setValue("middleName", parsedData.middleName, opts);
											editMiddleName(parsedData.middleName);
										}
										if (parsedData.lastName) {
											setValue("lastName", parsedData.lastName, opts);
											editLastName(parsedData.lastName);
										}

										// ─── Address ──────────────────────────────────────────────────────
										// Update RHF
										if (parsedData.address)
											setValue("currentStreet", parsedData.address, opts);
										if (parsedData.city)
											setValue("currentCity", parsedData.city, opts);
										if (parsedData.state)
											setValue("currentState", parsedData.state, opts);
										if (parsedData.zip)
											setValue("currentZip", parsedData.zip, opts);
										// Update store in one shot
										if (
											parsedData.address ||
											parsedData.city ||
											parsedData.state ||
											parsedData.zip
										) {
											editCurrentAddress({
												street: parsedData.address ?? currentAddress.street,
												city: parsedData.city ?? currentAddress.city,
												state: parsedData.state ?? currentAddress.state,
												zip: parsedData.zip ?? currentAddress.zip,
											});
										}

										// ─── License Number ───────────────────────────────────────────────
										if (parsedData.licenseNumber) {
											setValue("license", parsedData.licenseNumber, opts);
											editLicense(parsedData.licenseNumber);
										}

										// ─── Dates ─────────────────────────────────────────────────────────
										// DOB comes in as `dob`
										if (parsedData.dob) {
											setValue("birthDate", parsedData.dob, opts);
											editBirthDate(parsedData.dob);
										}
										// Expiration comes in as `expiration`
										if (parsedData.expiration) {
											setValue("licExpires", parsedData.expiration, opts);
											editLicExpires(parsedData.expiration);
										}

										// ─── Gender ────────────────────────────────────────────────────────
										if (parsedData.gender) {
											setValue("gender", parsedData.gender, opts);
											editGender(parsedData.gender);
										}
									}}
								/>
							) : (
								<button
									onClick={handleShowScanner}
									className="btn btn-outline-primary mb-3"
								>
									Scan License
								</button>
							)}
						</div>
					</div>
					<div>
						<Row>
							{saleType !== SaleTypeEnum.WHOLESALE.value &&
							saleType !== SaleTypeEnum.BUSINESS.value ? (
								<div className="col">
									<div role="group" className="btn-group">
										{type === 0 ? (
											<button
												type="button"
												className="btn-round btn-sm btn btn-outline-secondary-active"
												onClick={() => {
													editType(0);
												}}
											>
												Personal
											</button>
										) : (
											<button
												type="button"
												className="btn-round btn-sm btn btn-outline-secondary"
												onClick={() => {
													editType(0);
												}}
											>
												Personal
											</button>
										)}
										{type === 1 ? (
											<button
												type="button"
												className="btn-round btn-sm btn btn-outline-secondary-active"
												onClick={() => {
													editType(1);
												}}
											>
												Business
											</button>
										) : (
											<button
												type="button"
												className="btn-round btn-sm btn btn-outline-secondary"
												onClick={() => {
													editType(1);
												}}
											>
												Business
											</button>
										)}
									</div>
								</div>
							) : null}
						</Row>
					</div>
				</div>
				<div className="col">
					{saleType !== SaleTypeEnum.WHOLESALE.value &&
					saleType !== SaleTypeEnum.BUSINESS.value ? (
						<>
							{type === 0 || type === null ? (
								<CustomerDetails />
							) : (
								<BusinessDetails />
							)}
						</>
					) : (
						<BusinessDetails />
					)}
				</div>
				<div className="col">
					<CurrentAddress getCities={handleZipChange} />
					<ChildrenModal
						modal={cityModal}
						toggle={toggleCityModal}
						modalTitle="Cities"
					>
						<ZipCityTable
							data={addressData}
							toggle={toggleCityModal}
							address={currentAddress}
							setAddress={editCurrentAddress}
						/>
					</ChildrenModal>
				</div>
				<div className={isDeal ? "" : "float-button"}>
					<SaveNewCustomerButton updateDeal={updateDeal} />
				</div>
			</FormProvider>
		</div>
	);
};

export default ContactAddPage;
